import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Check, Tag, MapPin, Globe, ScrollText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { COUNTRIES, getRegionsByCountry } from '../constants/locations';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    suburb: '',
    state: '',
    country: 'AU',
    promo_code: ''
  });

  const regions = getRegionsByCountry(formData.country);

  const handleCountryChange = (country) => {
    setFormData({ ...formData, country, state: '' }); // Reset state when country changes
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    
    if (!firstName || !lastName || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields including first and last name');
      return;
    }
    
    if (firstName.length < 2 || lastName.length < 2) {
      toast.error('First and last name must each be at least 2 letters');
      return;
    }
    
    if (!/^[A-Za-z][A-Za-z\s\-'.]+$/.test(firstName) || !/^[A-Za-z][A-Za-z\s\-'.]+$/.test(lastName)) {
      toast.error('Name can only contain letters, spaces, hyphens, and apostrophes');
      return;
    }
    
    const fullName = `${firstName} ${lastName}`;
    if (/crafters?\s*market/i.test(fullName)) {
      toast.error('This name is not allowed. Please use your real name.');
      return;
    }

    if (!formData.phone || formData.phone.replace(/\D/g, '').length < 8) {
      toast.error('A valid mobile number is required');
      return;
    }

    if (!formData.suburb || !formData.state) {
      toast.error('Please enter your suburb and region');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!agreedToTerms) {
      toast.error('You must agree to the Terms of Service to register');
      return;
    }

    setLoading(true);
    try {
      const location = `${formData.suburb}, ${formData.state}, ${formData.country}`;
      await register(
        fullName,
        formData.email, 
        formData.password, 
        formData.phone, 
        location, 
        formData.promo_code,
        formData.suburb,
        formData.state,
        formData.country
      );
      toast.success('Welcome to Crafters Market!');
      
      if (formData.promo_code && formData.promo_code.trim()) {
        navigate('/setup-trial');
      } else {
        navigate('/browse');
      }
    } catch (err) {
      console.error('Registration failed', err);
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Free to browse & buy',
    'Message sellers directly',
    'Sellers: 30-day free trial',
    'No commission on sales'
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex">
      {/* Left Panel - Benefits */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-r from-sky-500 to-teal-500 text-white p-12 flex-col justify-center">
        <div className="max-w-md">
          <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Join Crafters Market
          </h2>
          <p className="text-sky-100 mb-8 text-lg leading-relaxed">
            Discover unique handcrafted and refurbished treasures from local artisans. 
            Free to browse, buy, and connect with sellers.
          </p>
          <ul className="space-y-4">
            {benefits.map((benefit, idx) => (
              <li key={idx} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                  <Check className="h-3 w-3" />
                </div>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Crafters Market
              </h1>
            </Link>
          </div>

          {/* Card */}
          <div className="bg-white border border-stone-200 rounded-sm p-8">
            <h2 className="text-2xl font-bold text-stone-900 text-center mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Create an Account
            </h2>
            <p className="text-stone-500 text-center text-sm mb-6">
              Free to join. Start selling whenever you're ready.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="Jane"
                    autoComplete="given-name"
                    className="mt-1"
                    data-testid="first-name-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Smith"
                    autoComplete="family-name"
                    className="mt-1"
                    data-testid="last-name-input"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  className="mt-1"
                  data-testid="email-input"
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Mobile Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="04XX XXX XXX"
                  autoComplete="tel"
                  className="mt-1"
                  data-testid="phone-input"
                  required
                />
              </div>

              <div>
                <Label htmlFor="password">Password *</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="At least 6 characters"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="country" className="flex items-center gap-1">
                  <Globe className="h-3.5 w-3.5 text-stone-400" />
                  Country *
                </Label>
                <Select 
                  value={formData.country} 
                  onValueChange={handleCountryChange}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country.value} value={country.value}>
                        {country.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="suburb" className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5 text-stone-400" />
                  Suburb/City *
                </Label>
                <Input
                  id="suburb"
                  type="text"
                  value={formData.suburb}
                  onChange={(e) => setFormData({ ...formData, suburb: e.target.value })}
                  placeholder={formData.country === 'NZ' ? "e.g., Ponsonby, Newmarket" : "e.g., Bondi, Richmond"}
                  className="mt-1"
                  data-testid="suburb-input"
                />
              </div>

              <div>
                <Label htmlFor="state">{formData.country === 'NZ' ? 'Region' : 'State'} *</Label>
                <Select 
                  value={formData.state} 
                  onValueChange={(value) => setFormData({ ...formData, state: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={`Select ${formData.country === 'NZ' ? 'region' : 'state'}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((region) => (
                      <SelectItem key={region.value} value={region.value}>
                        {region.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-400 mt-1">Helps connect you with local sellers and tradies</p>
              </div>

              <div>
                <Label htmlFor="promo_code" className="flex items-center gap-2">
                  <Tag className="h-3.5 w-3.5 text-stone-400" />
                  Promo Code (optional)
                </Label>
                <Input
                  id="promo_code"
                  type="text"
                  value={formData.promo_code}
                  onChange={(e) => setFormData({ ...formData, promo_code: e.target.value.toUpperCase() })}
                  placeholder="Enter code if you have one"
                  className="mt-1"
                  data-testid="promo-code-input"
                />
                <p className="text-xs text-stone-400 mt-1">Have a special code? Enter it here to unlock benefits.</p>
              </div>

              {/* Terms Agreement */}
              <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-200">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="terms"
                    checked={agreedToTerms}
                    onCheckedChange={setAgreedToTerms}
                    className="mt-0.5 data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"
                    data-testid="terms-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="terms" className="text-sm text-stone-700 cursor-pointer leading-relaxed">
                      I have READ and UNDERSTOOD the{' '}
                      <Link 
                        to="/terms" 
                        target="_blank"
                        className="text-teal-600 hover:text-teal-700 underline font-medium"
                      >
                        Terms of Service
                      </Link>
                      {' '}and{' '}
                      <Link 
                        to="/privacy" 
                        target="_blank"
                        className="text-teal-600 hover:text-teal-700 underline font-medium"
                      >
                        Privacy Policy
                      </Link>
                      , and I agree to be bound by them.
                    </Label>
                  </div>
                </div>
                <div className="mt-3 pl-7 text-xs text-amber-700 space-y-1">
                  <p className="font-medium">By checking this box, you confirm you understand:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-600">
                    <li>Your obligations under Australian Consumer Law</li>
                    <li>Prohibited items and safety requirements</li>
                    <li>Intellectual property rules and copyright compliance</li>
                    <li>How we collect, use, and protect your personal data</li>
                    <li>Subscription fees and refund policy</li>
                  </ul>
                  <p className="text-amber-700 font-medium pt-1">Please read the T&C carefully before ticking this box.</p>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || !agreedToTerms}
                className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-medium tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="register-btn"
              >
                {loading ? (
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-stone-500 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-teal-600 hover:underline font-medium">
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
